import { ReactElement, ReactNode } from 'react';

interface AuxPlotCardProps {
  title: string;
  /** Small grey caption under the title, e.g. the applied aux time offset. */
  note?: ReactNode;
  /** Control rendered to the right of the title (e.g. a mode toggle). */
  headerExtra?: ReactNode;
  /** Full-width control row between the header and the chart. */
  controls?: ReactNode;
  isLoading?: boolean;
  loadingLabel?: string;
  /** When true, the empty message replaces the chart. */
  isEmpty?: boolean;
  emptyMessage?: ReactNode;
  children: ReactNode;
}

/**
 * The card shell every plot on the Aux Sensor tab sits in: title row, optional
 * note/controls rows, and the loading and empty states. Previously each of the
 * four plots carried its own copy of this markup, which had already drifted
 * (differing heading margins and centred-vs-left empty text).
 */
export default function AuxPlotCard({
  title,
  note,
  headerExtra,
  controls,
  isLoading = false,
  loadingLabel = 'Loading…',
  isEmpty = false,
  emptyMessage,
  children,
}: AuxPlotCardProps): ReactElement {
  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
      <div className="cr-mb-2">
        <div className="cr-flex cr-items-center cr-justify-between cr-gap-4">
          <h2 className="cr-text-lg cr-font-semibold cr-text-blue-900">{title}</h2>
          {headerExtra}
        </div>
        {note ? <p className="cr-mt-1 cr-text-xs cr-text-gray-500">{note}</p> : null}
      </div>
      {controls}
      {isLoading ? (
        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-gap-2 cr-text-gray-400">
          <div
            className="cr-h-6 cr-w-6 cr-animate-spin cr-rounded-full cr-border-2
              cr-border-cirrusBlue-200 cr-border-t-cirrusBlue-400"
          />
          <span className="cr-text-sm">{loadingLabel}</span>
        </div>
      ) : isEmpty ? (
        <div
          className="cr-flex cr-h-32 cr-items-center cr-justify-center cr-text-center
            cr-text-sm cr-text-gray-400"
        >
          {emptyMessage}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
